import {
  Grid,
  Typography,
  Badge,
  Card,
  CardActionArea,
  Link,
  NotificationsIcon,
  Avatar,
  Menu,
  MenuItem,
} from '@rbx/ui';
import React, { FunctionComponent, useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { CreatorStreamNotification } from '@rbx/clients/creatorNotificationStreamsApi';
import { Thumbnail2d, ThumbnailTypes } from '@rbx/thumbnails';
import { useTranslation } from '@rbx/intl';
import Dompurify from 'dompurify';
import useNotificationStyles from './NotificationV2.styles';
import useNavigationConfigs from '../../hooks/useNavigationConfigs';
import { clickNotificationEventModel } from '../../event/eventConstants';
import useElapsedTime from '../utils/useElapsedTime';
import useNotificationImpressionTracker from '../utils/useNotificationImpressionTracker';
import NotificationTargetType from '../utils/NotificationTargetType';
import NOTIFICATION_ICON_MAP from '../utils/notificationIconMap';

type TNotificationProps = {
  notificationContent: CreatorStreamNotification;
  markRead?: (index: number) => void;
  notificationGroupIndex?: number;
};

const NotificationLinkWrapper: FunctionComponent<{
  notification: CreatorStreamNotification;
  onNotificationClick: () => void;
  children?: React.ReactNode;
}> = ({ notification, onNotificationClick, children }) => {
  if (notification?.creatorStreamNotificationContent?.clickAction) {
    return (
      <CardActionArea onClick={onNotificationClick}>
        <Link
          href={notification?.creatorStreamNotificationContent?.clickAction}
          color='inherit'
          target='_blank'
          underline='none'>
          {children}
        </Link>
      </CardActionArea>
    );
  }

  return <React.Fragment>{children}</React.Fragment>;
};

const Notification: FunctionComponent<TNotificationProps> = ({
  notificationContent,
  markRead,
  notificationGroupIndex,
}) => {
  const { translate } = useTranslation();
  const { sendEvent } = useNavigationConfigs();
  const { classes: styles, cx } = useNotificationStyles();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isMultiLine, setIsMultiLine] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);
  const impressionRef = useRef<HTMLDivElement>(null);

  const onNotificationClick = useCallback(() => {
    sendEvent(clickNotificationEventModel(notificationContent, notificationGroupIndex));
  }, [notificationContent, sendEvent, notificationGroupIndex]);
  const elapsedTime = useElapsedTime((notificationContent.createdUtcTimeInMs ?? 0) / 1000, "Label.ElapsedTimeShort");

  // Track impression when the notification enters viewport
  useNotificationImpressionTracker(impressionRef, notificationContent, sendEvent, notificationGroupIndex);

  // Note @neoxu (10/28/2025): Not included in M1 release.
  // const handleMenuClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
  //   event.stopPropagation();
  //   event.preventDefault();
  //   setMenuAnchor(event.currentTarget);
  // }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleTurnOff = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    // TODO: Implement turn off notification functionality
    handleMenuClose();
  }, [handleMenuClose]);
  const NotificationIcon = useMemo(() => {
    const targetId = notificationContent?.creatorStreamNotificationContent?.targetId;
    switch (notificationContent?.creatorStreamNotificationContent?.targetType) {
      case NotificationTargetType.Universe:
        return (
          <Avatar
            alt={translate('Label.PlaceIconAltString')}
            variant='rounded'
            className={cx(styles.thumbnailContainer)}>
            <Thumbnail2d
              targetId={Number(targetId) ?? 0}
              type={ThumbnailTypes.gameIcon}
              alt={translate('Label.PlaceIconAltString')}
            />
          </Avatar>
        );
      case NotificationTargetType.User:
        return (
          <Avatar
            alt={translate('Label.UserIconAltString')}
            variant='rounded'
            className={cx(styles.thumbnailContainer)}>
            <Thumbnail2d
              targetId={Number(targetId) ?? 0}
              type={ThumbnailTypes.avatarHeadshot}
              alt={translate('Label.UserIconAltString')}
            />
          </Avatar>
        );
      case NotificationTargetType.Static: {
        const staticIcon =
          NOTIFICATION_ICON_MAP[notificationContent?.creatorStreamNotificationContent?.targetId];
        if (staticIcon) {
          return staticIcon;
        }

        return <NotificationsIcon color='action' />;
      }
      default:
        return <NotificationsIcon color='action' />;
    }
  }, [
    cx,
    notificationContent?.creatorStreamNotificationContent?.targetId,
    notificationContent?.creatorStreamNotificationContent?.targetType,
    styles.thumbnailContainer,
    translate,
  ]);

  const sanitizedNotificationTitle = useMemo(() => {
    return Dompurify.sanitize(`${notificationContent?.creatorStreamNotificationContent?.title}`, {
      ALLOWED_TAGS: ['b', 'i', 'u', 's', 'br'],
      ALLOWED_ATTR: [],
    });
  }, [notificationContent?.creatorStreamNotificationContent?.title]);

  const hasBodyText = !!notificationContent?.creatorStreamNotificationContent?.body;

  // Detect if title wraps to multiple lines or if body text exists
  useEffect(() => {
    if (titleRef.current) {
      const lineHeight = parseFloat(getComputedStyle(titleRef.current).lineHeight) || 19.6; // 14px * 1.4 line-height
      const height = titleRef.current.offsetHeight;
      // Title is multiline if height exceeds ~1.5x line height (accounting for minor variations)
      const isTitleMultiLine = height > lineHeight * 1.5;
      setIsMultiLine(isTitleMultiLine || hasBodyText);
    }
  }, [sanitizedNotificationTitle, hasBodyText]);

  return (
    <Card classes={{ root: styles.cardRadius }}>
      <NotificationLinkWrapper
        notification={notificationContent}
        onNotificationClick={onNotificationClick}>
        <Grid
          ref={impressionRef}
          className={cx(styles.container, isMultiLine && styles.containerMultiLine)}
          container
          onClick={() => {
            if (markRead && notificationGroupIndex !== undefined && !notificationContent.read) {
              markRead(notificationGroupIndex);
            }
          }}>
          <Grid item XSmall='auto'>
            {NotificationIcon}
          </Grid>
          <Grid item XSmall className={styles.notificationText} zeroMinWidth>
            <Grid container direction='column'>
              <Typography
                ref={titleRef}
                variant='body1'
                className={styles.truncatedText}
                dangerouslySetInnerHTML={{
                  __html: sanitizedNotificationTitle,
                }}
              />
              {notificationContent?.creatorStreamNotificationContent?.body && (
                <Typography variant='body2' color='secondary' className={styles.truncatedText}>
                  {notificationContent.creatorStreamNotificationContent.body}
                </Typography>
              )}
            </Grid>
          </Grid>
          <Grid item XSmall='auto'>
            <Grid container alignItems='center' className={styles.rightSection}>
              <div className={styles.badgeContainer}>
                {!notificationContent.read &&
                  notificationContent?.creatorStreamNotificationContent?.clickAction && (
                    <Badge variant='dot' />
                  )}
                {!notificationContent?.creatorStreamNotificationContent?.clickAction && (
                  <Badge variant='dot' classes={{ badge: styles.disabledDot }} />
                )}
              </div>
              <Typography color='secondary' className={styles.timeText}>
                {elapsedTime}
              </Typography>
              {/* 
              Note @neoxu (10/28/2025): Not included in M1 release.
              <IconButton
                aria-label={translate('Label.NotificationMenuAria')}
                className={styles.menuButton}
                onClick={handleMenuClick}
                size='small'>
                <MoreVertIcon fontSize='small' color='secondary' />
              </IconButton> */}
            </Grid>
          </Grid>
        </Grid>
      </NotificationLinkWrapper>
      <Menu
        id={`notification-menu-${notificationGroupIndex ?? 0}-${notificationContent.createdUtcTimeInMs ?? 0}`}
        variant='modal'
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        MenuListProps={{
          'aria-labelledby': translate('Label.NotificationMenuAria'),
        }}>
        <MenuItem variant='standardMenu' onClick={handleTurnOff}>
          <Typography variant='body1' color='primary'>
            {translate('Action.TurnOffNotifications')}
          </Typography>
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default Notification;
